/**
 * External dependencies
 */
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import { ScrollArea } from '@art-of-hospitality-solutions-llc/ui-kit/components/scroll-area';
import { useMemo, useState } from 'react';
import { Trash2Icon } from 'lucide-react';

/**
 * Internal dependencies
 */
import Breadcrumbs from '@Components/Breadcrumbs';
import NotFound from '@Components/NotFound';
import RichText from '@Components/RichText';
import { CloudinaryImage } from '@art-of-hospitality-solutions-llc/ui-kit/components/cloudinary-image';
import {
  Card,
  CardContent,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import { Link, useParams } from 'react-router-dom';
import EditSpaceSheet from '../../../components/Spaces/EditSpaceDetailsSheet';
import EditSpaceVenueSheet from '../../../components/Spaces/EditSpaceVenueSheet';
import EditSpacePrivateDiningSheet from '../../../components/Spaces/EditSpacePrivateDiningSheet';
import EditSpaceCorporateSheet from '../../../components/Spaces/EditSpaceCorporateSheet';
import { useSpace } from '../../../hooks/useSpace';
import { useUpdateSpace } from '../../../hooks/useUpdateSpace';
import FeaturedImageDialog from '../../../components/FeaturedImageDialog';
import ManageSpaceImagesDialog from '../../../components/ManageSpaceImagesDialog';
import { toastSuccess } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { useQueryClient } from '@tanstack/react-query';
import ShowFor from '@Components/ShowFor';
import { Badge } from '@art-of-hospitality-solutions-llc/ui-kit/components/badge';

function RichTextField({
  label,
  content,
  emptyText,
}: {
  label: string;
  content: string | null;
  emptyText: string;
}) {
  return (
    <div>
      <div className="mb-1 text-sm font-bold text-zinc-500">{label}</div>
      {content ? (
        <div className="rounded-xl bg-zinc-100 p-4">
          <ScrollArea className="max-h-96 overflow-auto">
            <RichText content={content} className="max-w-full!" />
          </ScrollArea>
        </div>
      ) : (
        <div className="flex h-32 flex-col items-center justify-center rounded border border-dashed border-zinc-300 bg-zinc-50 text-center">
          <div className="text-sm text-zinc-500">{emptyText}</div>
        </div>
      )}
    </div>
  );
}

function BookingStatus({
  label,
  description,
  enabled,
}: {
  label: string;
  description: string;
  enabled: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl border border-zinc-200 bg-zinc-50 p-4">
      <div>
        <div className="text-sm font-bold text-zinc-500">{label}</div>
        <div className="mt-0.5 text-xs text-zinc-500">{description}</div>
      </div>
      <Badge variant={enabled ? 'default' : 'outline'} className="shrink-0">
        {enabled ? 'On' : 'Off'}
      </Badge>
    </div>
  );
}

export default function SpaceDetails() {
  const { spaceId } = useParams();
  const {
    data: space,
    status,
    isError,
  } = useSpace({
    spaceId,
    include: 'venue,assets.asset,floorPlanAssets.asset,featuredAsset',
  });

  const [spaceSheetOpen, setSpaceSheetOpen] = useState(false);
  const [spaceVenueSheetOpen, setSpaceVenueSheetOpen] = useState(false);
  const [pdSheetOpen, setPdSheetOpen] = useState(false);
  const [corpSheetOpen, setCorpSheetOpen] = useState(false);
  const [featuredImageDialogOpen, setFeaturedImageDialogOpen] = useState(false);
  const [galleryDialogOpen, setGalleryDialogOpen] = useState(false);
  const [floorPlansDialogOpen, setFloorPlansDialogOpen] = useState(false);

  const queryClient = useQueryClient();
  const updateSpaceMutation = useUpdateSpace({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['space'],
        exact: false,
      });
      queryClient.invalidateQueries({ queryKey: ['spaces'] });
    },
  });

  const spaceAssets = useMemo(() => {
    if (!space || !space.assets) return [];
    return space?.assets?.map((img) => ({
      ...img.asset,
      id: img.id,
    }));
  }, [space]);

  const floorPlanAssets = useMemo(() => {
    if (!space || !space.floorPlanAssets) return [];
    return space?.floorPlanAssets?.map((img) => ({
      ...img.asset,
      id: img.id,
    }));
  }, [space]);

  if (isError) {
    return <NotFound />;
  }
  if (status === 'pending' || !space) {
    return <div>Loading...</div>;
  }

  return (
    <div className="w-full">
      <Breadcrumbs
        navList={[
          { name: 'Locations', href: '/locations' },
          { name: 'Spaces', href: '/spaces' },
          { name: space.name, href: `/spaces/${spaceId}` },
        ]}
      />

      <div className="mt-8 mb-4 flex items-end">
        <Heading>{space.name}</Heading>
        <p className="mx-2 mb-1 text-sm font-bold text-zinc-500">SPACE</p>
      </div>

      <div className="grid grid-cols-1 gap-12 xl:grid-cols-3">
        <div className="flex flex-col space-y-8 xl:col-span-2">
          <div>
            <div className="mb-2 flex items-center justify-between">
              <Heading level="h3">Details</Heading>
              <div className="flex items-center space-x-2">
                <ShowFor permissions={['SPACES_WRITE']}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSpaceSheetOpen(true)}
                  >
                    Edit
                  </Button>
                </ShowFor>
              </div>
            </div>
            <Card>
              <CardContent>
                <div className="relative">
                  <div className="flex flex-col space-y-2.5">
                    <div>
                      <div className="text-sm font-bold text-zinc-500">
                        Space Name
                      </div>
                      {space.name}
                      <br />
                      {space.disabledAt ? (
                        <Badge variant="destructive">Disabled</Badge>
                      ) : (
                        <Badge variant="outline">Enabled</Badge>
                      )}
                    </div>
                    <div>
                      <div className="text-sm font-bold text-zinc-500">
                        Teaser
                      </div>
                      {space.teaser ? (
                        <p className="mt-1">{space.teaser}</p>
                      ) : (
                        <span className="text-zinc-500">No teaser added</span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4 md:grid">
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          Capacity
                        </div>
                        {space.capacity}
                      </div>
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          Guest minimum
                        </div>
                        {space.guestMin}
                      </div>
                    </div>
                    <div>
                      <div className="mb-2 text-sm font-bold text-zinc-500">
                        Description
                      </div>
                      {space.description ? (
                        <div className="rounded-xl bg-zinc-100 p-4">
                          <ScrollArea className="h-96 overflow-auto">
                            <RichText
                              content={space.description}
                              className="!max-w-full"
                            />
                          </ScrollArea>
                        </div>
                      ) : (
                        <span className="text-zinc-500">
                          No description added
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <Heading level="h3">Location</Heading>
              <div className="flex items-center space-x-2">
                <ShowFor permissions={['SPACES_WRITE']}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSpaceVenueSheetOpen(true)}
                  >
                    Edit
                  </Button>
                </ShowFor>
              </div>
            </div>
            <Card>
              <CardContent>
                <div className="grid grid-cols-2">
                  <div className="flex flex-col space-y-2.5">
                    <div>
                      <div className="text-sm font-bold text-zinc-500">
                        Venue
                      </div>
                      {space.venue && space.venueId ? (
                        <Link to={`/venues/${space.venue.id}`}>
                          {space.venue.name}
                        </Link>
                      ) : (
                        <span className="text-zinc-500">No venue assigned</span>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <Heading level="h3">Private Dining Details</Heading>
              <div className="flex items-center space-x-2">
                <ShowFor permissions={['SPACES_WRITE']}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPdSheetOpen(true)}
                  >
                    Edit
                  </Button>
                </ShowFor>
              </div>
            </div>
            <Card>
              <CardContent>
                <div className="space-y-4">
                  <BookingStatus
                    label="Private Dining Booking"
                    description="Controls whether this space appears in the private dining section of the events and members apps, when all other conditions are met."
                    enabled={
                      space.bookingTypes?.includes('PRIVATE_DINING') ?? false
                    }
                  />
                  <RichTextField
                    label="Details"
                    content={space.pdDetails}
                    emptyText="No private dining details added"
                  />
                  <RichTextField
                    label="Terms"
                    content={space.pdTerms}
                    emptyText="No private dining terms added"
                  />
                  <RichTextField
                    label="Policy"
                    content={space.pdPolicy}
                    emptyText="No private dining policy added"
                  />
                  <RichTextField
                    label="Beverage Options"
                    content={space.pdBeverageOptions}
                    emptyText="No private dining beverage options added"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <Heading level="h3">Corporate Details</Heading>
              <div className="flex items-center space-x-2">
                <ShowFor permissions={['SPACES_WRITE']}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCorpSheetOpen(true)}
                  >
                    Edit
                  </Button>
                </ShowFor>
              </div>
            </div>
            <Card>
              <CardContent>
                <div className="space-y-4">
                  <BookingStatus
                    label="Corporate Booking"
                    description="Controls whether this space appears in the corporate section of the events and members apps, when all other conditions are met."
                    enabled={space.bookingTypes?.includes('CORPORATE') ?? false}
                  />
                  <RichTextField
                    label="Details"
                    content={space.corpDetails}
                    emptyText="No corporate details added"
                  />
                  <RichTextField
                    label="Terms"
                    content={space.corpTerms}
                    emptyText="No corporate terms added"
                  />
                  <RichTextField
                    label="Policy"
                    content={space.corpPolicy}
                    emptyText="No corporate policy added"
                  />
                  <RichTextField
                    label="Beverage Options"
                    content={space.corporateBeverageOptions}
                    emptyText="No corporate beverage options added"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        <div className="flex flex-col space-y-8">
          {/* Photos */}
          <div>
            <div className="mb-2">
              <Heading level="h3">Photos</Heading>
            </div>
            <Card>
              <CardContent>
                <div className="space-y-6">
                  {/* Featured Image Section */}
                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <div className="text-sm font-medium">Featured Image</div>
                      <ShowFor permissions={['SPACES_WRITE']}>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setFeaturedImageDialogOpen(true)}
                        >
                          Edit featured image
                        </Button>
                      </ShowFor>
                    </div>
                    {space.featuredAsset ? (
                      <div className="relative flex min-h-42 items-center justify-center overflow-hidden rounded-md border border-zinc-200 bg-zinc-100">
                        <CloudinaryImage
                          publicId={space.featuredAsset.cloudinaryId}
                          mode="original"
                          className="h-64 w-full object-contain"
                        />
                        <ShowFor permissions={['SPACES_WRITE']}>
                          <Button
                            variant="outline"
                            size="icon"
                            className="absolute top-2 right-2"
                            disabled={updateSpaceMutation.isPending}
                            onClick={() =>
                              updateSpaceMutation.mutate(
                                {
                                  spaceId: space.id,
                                  payload: { featuredAssetId: null },
                                },
                                {
                                  onSuccess: () =>
                                    toastSuccess('Featured image removed.'),
                                },
                              )
                            }
                          >
                            <Trash2Icon className="h-4 w-4" />
                          </Button>
                        </ShowFor>
                      </div>
                    ) : (
                      <div className="flex h-32 flex-col items-center justify-center rounded border border-dashed border-zinc-300 bg-zinc-50 text-center">
                        <div className="text-sm text-zinc-500">
                          No featured image set
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Other Images Section */}
                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <div className="text-sm font-medium">Gallery</div>
                      <ShowFor permissions={['SPACES_WRITE']}>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setGalleryDialogOpen(true)}
                        >
                          Manage images
                        </Button>
                      </ShowFor>
                    </div>
                    {spaceAssets.length === 0 ? (
                      <div className="flex h-32 flex-col items-center justify-center rounded border border-dashed border-zinc-300 bg-zinc-50 text-center">
                        <div className="text-sm text-zinc-500">
                          No photos uploaded
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-2 2xl:grid-cols-3">
                        {spaceAssets.map((asset) => {
                          return (
                            <div
                              key={asset.id}
                              className="relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-md border border-zinc-200 bg-zinc-100"
                            >
                              {asset ? (
                                <CloudinaryImage
                                  publicId={asset.cloudinaryId}
                                  mode="original"
                                  className="h-full w-full object-contain"
                                />
                              ) : (
                                <div className="text-xs text-zinc-500">
                                  Loading...
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Floor Plan Images */}
          <div>
            <div className="mb-2">
              <Heading level="h3">Floor Plans</Heading>
            </div>
            <Card>
              <CardContent>
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-sm font-medium">Floor Plan Images</div>
                  <ShowFor permissions={['SPACES_WRITE']}>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setFloorPlansDialogOpen(true)}
                    >
                      Manage floor plans
                    </Button>
                  </ShowFor>
                </div>
                {floorPlanAssets.length === 0 ? (
                  <div className="flex h-32 flex-col items-center justify-center rounded border border-dashed border-zinc-300 bg-zinc-50 text-center">
                    <div className="text-sm text-zinc-500">
                      No floor plan images uploaded
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-2 2xl:grid-cols-3">
                    {floorPlanAssets.map((asset) => {
                      return (
                        <div
                          key={asset.id}
                          className="relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-md border border-zinc-200 bg-zinc-100"
                        >
                          {asset ? (
                            <CloudinaryImage
                              publicId={asset.cloudinaryId}
                              mode="original"
                              className="h-full w-full object-contain"
                            />
                          ) : (
                            <div className="text-xs text-zinc-500">
                              Loading...
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <EditSpaceSheet
        space={space}
        isOpen={spaceSheetOpen}
        setIsOpen={setSpaceSheetOpen}
      />
      <EditSpaceVenueSheet
        space={space}
        isOpen={spaceVenueSheetOpen}
        setIsOpen={setSpaceVenueSheetOpen}
      />
      <EditSpacePrivateDiningSheet
        space={space}
        isOpen={pdSheetOpen}
        setIsOpen={setPdSheetOpen}
      />
      <EditSpaceCorporateSheet
        space={space}
        isOpen={corpSheetOpen}
        setIsOpen={setCorpSheetOpen}
      />

      <FeaturedImageDialog
        name={space.name}
        resourceType="space"
        currentFeaturedAssetId={space.featuredAssetId}
        isOpen={featuredImageDialogOpen}
        onOpenChange={setFeaturedImageDialogOpen}
        onSave={async (assetId) => {
          await updateSpaceMutation.mutateAsync({
            spaceId: space.id,
            payload: { featuredAssetId: assetId },
          });
          toastSuccess('Featured image updated successfully.');
        }}
      />

      <ManageSpaceImagesDialog
        name={space.name}
        currentAssetIds={space.assets?.map((sa) => sa.assetId) ?? []}
        isOpen={galleryDialogOpen}
        onOpenChange={setGalleryDialogOpen}
        onSave={async (assetIds) => {
          await updateSpaceMutation.mutateAsync({
            spaceId: space.id,
            payload: { assets: assetIds },
          });
          toastSuccess('Gallery images updated successfully.');
        }}
      />

      <ManageSpaceImagesDialog
        name={`${space.name} floor plans`}
        currentAssetIds={space.floorPlanAssets?.map((sa) => sa.assetId) ?? []}
        isOpen={floorPlansDialogOpen}
        onOpenChange={setFloorPlansDialogOpen}
        onSave={async (assetIds) => {
          await updateSpaceMutation.mutateAsync({
            spaceId: space.id,
            payload: { floorPlanAssets: assetIds },
          });
          toastSuccess('Floor plans updated successfully.');
        }}
      />
    </div>
  );
}
